import * as URI from 'urijs';

import {
  OnlineResource,
  OnlineResourceGoogleBooks,
  OnlineResourceGoogleBooksType,
  OnlineResourceType,
  OnlineResourceUrl,
  OnlineResourceUrlType,
} from '®shared/bibliographic-resource';
import { ResourceEvent } from '®shared/events';

/**
 * A symbol describing the position of a link within the UI
 */
export enum LinkPositionOnPage {
  ITEM_AVAILABILITY_SECTION = 'item_availability_section',
  VIEW_ONLINE_BUTTON = 'view_online_button',
}

/**
 * A broad classification of link destinations
 */
export enum DestinationType {
  BOOKSTORE = 'bookstore',
  CONTENT = 'content',
  PREVIEW = 'preview',
  LIBRARY_CATALOGUE = 'library_catalogue',
  RESOLVER = 'resolver',
}

const DEFAULT_GOOGLE_BOOKS_URL = 'https://books.google.com';

/**
 * A more fine-grained subclassification of link destinations, present on
 * all external-link-click events
 *
 * NOTE: bookstore subtypes are the labels for the bookstores themselves - bare strings
 */
export enum DestinationSubtype {
  // content subtypes
  CONTENT_DIGITISATION = 'digitisation',
  CONTENT_EBOOK = 'ebook',
  CONTENT_OTHER = 'other',
  // content preview
  PREVIEW_GOOGLE_BOOKS = 'google_books',
  // library catalogue subtypes
  LIBRARY_CATALOGUE_SEARCH = 'library_search',
  LIBRARY_CATALOGUE_LCN_LOOKUP = 'lcn_lookup',
  // resolver subtypes
  RESOLVER_DOI = 'doi',
  RESOLVER_OPENURL = 'openurl',
}

/**
 * Specific event properties that are not used as a primary filtering
 * mechanism. These properties will appear on a subset of the events only.
 */
export interface ExternalLinkOptionalArguments {
  is_player_resource?: boolean;
}

export interface ExternalLinkClickEvent extends ExternalLinkOptionalArguments {
  external_link_domain: string;
  destination_type: DestinationType;
  destination_subtype: string;
  position_on_page: LinkPositionOnPage;
}

export type OnlineResourceClickEvent = ExternalLinkClickEvent & ResourceEvent;

/**
 * Get an external-link-click event for an online-resource-button link.
 *
 * @param  {OnlineResource}         onlineResource   The online resource details
 * @param  {boolean}                isPlayerResource True for a Player resource; false otherwise
 * @param  {LinkPositionOnPage}     positionOnPage   The position within the page
 * @return {ExternalLinkClickEvent}                  An event instance
 */
export function getOnlineResourceEventProperties(
  onlineResource: OnlineResource,
  isPlayerResource: boolean,
  positionOnPage: LinkPositionOnPage,
): ExternalLinkClickEvent {
  switch (onlineResource.type) {
    case OnlineResourceGoogleBooksType.GOOGLE_BOOKS: {
      const onlineResourceGoogleBooks =
        onlineResource as OnlineResourceGoogleBooks;
      const { preview_url, type } = onlineResourceGoogleBooks;
      const googleBooksPreviewUrlOrDefault =
        preview_url || DEFAULT_GOOGLE_BOOKS_URL;
      return getExternalLinkClickEventProperties(
        googleBooksPreviewUrlOrDefault,
        positionOnPage,
        getDestinationTypeForOnlineResourceType(type),
        getDestinationSubtypeForOnlineResourceType(type),
      );
    }

    default: {
      const onlineResourceUrl = onlineResource as OnlineResourceUrl;
      const { type } = onlineResourceUrl;

      return getExternalLinkClickEventProperties(
        onlineResourceUrl.original_url,
        positionOnPage,
        getDestinationTypeForOnlineResourceType(type),
        getDestinationSubtypeForOnlineResourceType(type),
        { is_player_resource: isPlayerResource },
      );
    }
  }
}

/**
 * Get an external-link-click event for a generic link.
 *
 * @param  {string}                             url                The link URL
 * @param  {LinkPositionOnPage}                 positionOnPage     The position within the page
 * @param  {DestinationType}                    destinationType    The destination type
 * @param  {DestinationSubtype}            destinationSubtype The destination subtype
 * @param  {ExternalLinkOptionalArguments = {}} options            optional extra arguments
 * @return {ExternalLinkClickEvent}                                An event instance
 */
export function getExternalLinkClickEventProperties(
  url: string,
  positionOnPage: LinkPositionOnPage,
  destinationType: DestinationType,
  destinationSubtype: DestinationSubtype,
  options: ExternalLinkOptionalArguments = {},
): ExternalLinkClickEvent {
  return getRawExternalLinkClickEventProperties(
    url,
    positionOnPage,
    destinationType,
    destinationSubtype,
    options,
  );
}

/**
 * Get an external-link-click event for a bookstore link.
 *
 * @param  {string}                 bookstoreLabel The bookstore label (e.g. 'Amazon')
 * @param  {string}                 url            The URL for the book in question
 * @param  {LinkPositionOnPage}     positionOnPage The position within the page
 * @return {ExternalLinkClickEvent}                An event instance
 */
export function getBookstoreLinkClickEventProperties(
  bookstoreLabel: string,
  url: string,
  positionOnPage: LinkPositionOnPage = LinkPositionOnPage.ITEM_AVAILABILITY_SECTION,
): ExternalLinkClickEvent {
  return getRawExternalLinkClickEventProperties(
    url,
    positionOnPage,
    DestinationType.BOOKSTORE,
    bookstoreLabel,
  );
}

/**
 * Construct a valid, normalised external-link-click event.
 *
 * NOTE: this function is NOT exported, because we don't want people calling
 * it with any string values for the `destination_subtype` property. Rather,
 * they should be using `getExternalLinkClickEventProperties` with a constraint
 * of known values for the destination-subtype.
 *
 * @param  {string}                             url                 The link URL
 * @param  {LinkPositionOnPage}                 position_on_page    The position within the page
 * @param  {DestinationType}                    destination_type    The destination type
 * @param  {string}                             destination_subtype The destination subtype
 * @param  {ExternalLinkOptionalArguments = {}} options             optional extra arguments
 * @return {ExternalLinkClickEvent}                                 An event instance
 */
function getRawExternalLinkClickEventProperties(
  url: string,
  position_on_page: LinkPositionOnPage,
  destination_type: DestinationType,
  destination_subtype: string,
  options: ExternalLinkOptionalArguments = {},
): ExternalLinkClickEvent {
  const external_link_domain = getHostnameFromUrl(url);
  return {
    ...options,
    destination_type,
    destination_subtype,
    external_link_domain,
    position_on_page,
  };
}

function getHostnameFromUrl(originalUrl: string): string {
  const originalUri = new URI(originalUrl);
  if (originalUri.hostname()) {
    return originalUri.hostname();
  } else if (originalUri.path() === '/link') {
    // if URL is a redirection through the link-tracker,
    // extract the hostname from the link-tracker target URL
    const { url = null } = originalUri.search(true);
    if (url && (typeof url === 'string' || url instanceof String)) {
      return getHostnameFromUrl(url as string);
    }
  }
  return originalUrl;
}

function getDestinationTypeForOnlineResourceType(
  onlineResourceType: OnlineResourceType,
): DestinationType {
  switch (onlineResourceType) {
    case OnlineResourceGoogleBooksType.GOOGLE_BOOKS: {
      return DestinationType.PREVIEW;
    }
    case OnlineResourceUrlType.DOI:
    case OnlineResourceUrlType.OPENURL: {
      return DestinationType.RESOLVER;
    }
    default: {
      return DestinationType.CONTENT;
    }
  }
}

function getDestinationSubtypeForOnlineResourceType(
  onlineResourceType: OnlineResourceType,
): DestinationSubtype {
  switch (onlineResourceType) {
    case OnlineResourceUrlType.DIGITISATION: {
      return DestinationSubtype.CONTENT_DIGITISATION;
    }
    case OnlineResourceUrlType.DOI: {
      return DestinationSubtype.RESOLVER_DOI;
    }
    case OnlineResourceUrlType.EBOOK: {
      return DestinationSubtype.CONTENT_EBOOK;
    }
    case OnlineResourceGoogleBooksType.GOOGLE_BOOKS: {
      return DestinationSubtype.PREVIEW_GOOGLE_BOOKS;
    }
    case OnlineResourceUrlType.OPENURL: {
      return DestinationSubtype.RESOLVER_OPENURL;
    }
    case OnlineResourceUrlType.WEB: {
      return DestinationSubtype.CONTENT_OTHER;
    }
  }
}
